/**
 * Multi-select country filter for the events feed.
 *
 * Opens as a bottom sheet, matching the EventDetailSheet pattern
 * (cream background, grabber, drag-to-dismiss, backdrop tap, Esc).
 * Selections take effect in real time — no Apply button — and the
 * sheet stays open until the user dismisses it.
 *
 * Structure:
 *   • "Just my teams" — toggle that selects/deselects every followed
 *     country in one tap.
 *   • Following — each followed country as its own row.
 *   • All teams — every other country with at least one event in the
 *     feed, alphabetical.
 *   • Sticky bottom row: Clear + selected count.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Country, CountryCode } from '@/types/domain';
import { DATE_SCOPES, type DateScope } from '@/lib/eventDateScope';
import { haptics } from '@/lib/native';
import { FlagStripe } from '@/components/primitives';

interface Props {
  countries: Country[];
  follows: Partial<Record<CountryCode, boolean>>;
  selected: Set<CountryCode>;
  setSelected: (next: Set<CountryCode>) => void;
  /** Date scope lives here too — the funnel is the single filter home, with a
   * "When" section above Teams. */
  dateScope: DateScope;
  setDateScope: (next: DateScope) => void;
  onClose: () => void;
}

const DISMISS_THRESHOLD_PX = 100;

export function CountryFilterSheet({
  countries,
  follows,
  selected,
  setSelected,
  dateScope,
  setDateScope,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStartY = useRef<number | null>(null);

  const onDragStart = (e: ReactPointerEvent) => {
    dragStartY.current = e.clientY;
    setDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onDragMove = (e: ReactPointerEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.clientY - dragStartY.current;
    if (delta > 0) setDragY(delta);
  };
  const endDrag = () => {
    if (dragY > DISMISS_THRESHOLD_PX) onClose();
    else setDragY(0);
    dragStartY.current = null;
    setDragging(false);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const followed = useMemo(
    () => countries.filter((c) => follows[c.code]),
    [countries, follows],
  );
  const others = useMemo(
    () =>
      countries
        .filter((c) => !follows[c.code])
        .sort((a, b) => a.name.localeCompare(b.name)),
    [countries, follows],
  );

  const allFollowedSelected =
    followed.length > 0 && followed.every((c) => selected.has(c.code));

  const toggleJustMyTeams = () => {
    haptics.selection();
    const next = new Set(selected);
    if (allFollowedSelected) {
      for (const c of followed) next.delete(c.code);
    } else {
      for (const c of followed) next.add(c.code);
    }
    setSelected(next);
  };

  const toggle = (code: CountryCode) => {
    haptics.selection();
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setSelected(next);
  };

  const pickDateScope = (scope: DateScope) => {
    haptics.selection();
    setDateScope(scope);
  };

  const clearAll = () => {
    haptics.light();
    setSelected(new Set());
    setDateScope('all');
  };

  const nothingActive = selected.size === 0 && dateScope === 'all';

  return (
    <>
      <div role="presentation" onClick={onClose} style={backdropStyle} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('events:filterSheet.title')}
        style={{
          ...sheetStyle,
          transform: `translateY(${dragY}px)`,
          transition: dragging ? 'none' : 'transform 0.22s ease',
        }}
      >
        <div
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          style={{ touchAction: 'none', cursor: dragging ? 'grabbing' : 'grab' }}
        >
          <div style={grabberStyle} />
          <h2 style={headerStyle}>{t('events:filterSheet.title')}</h2>
        </div>

        <div style={contentStyle}>
          {/* When — single-select date scope. Lives here so the map header
              stays clean; drives both List and Map via the shared filter. */}
          <div style={sectionLabelStyle}>{t('events:filterSheet.when')}</div>
          <div style={whenRowStyle}>
            {DATE_SCOPES.map((id) => {
              const activeChip = dateScope === id;
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={activeChip}
                  onClick={() => pickDateScope(id)}
                  style={{ ...whenChipStyle, ...(activeChip ? whenChipActiveStyle : {}) }}
                >
                  {t(`events:dateScope.${id}`)}
                </button>
              );
            })}
          </div>

          {followed.length > 0 && (
            <button
              type="button"
              onClick={toggleJustMyTeams}
              style={{ ...justMyTeamsStyle, ...(allFollowedSelected ? justMyTeamsActiveStyle : {}) }}
            >
              <span>{t('events:filterSheet.justMyTeams')}</span>
              <span style={{ opacity: 0.7, fontSize: 13 }}>
                {followed.length}
              </span>
            </button>
          )}

          {followed.length > 0 && (
            <>
              <div style={sectionLabelStyle}>{t('events:filterSheet.following')}</div>
              <ul style={listStyle}>
                {followed.map((c) => (
                  <CountryRow
                    key={c.code}
                    country={c}
                    selected={selected.has(c.code)}
                    onToggle={() => toggle(c.code)}
                  />
                ))}
              </ul>
            </>
          )}

          {others.length > 0 && (
            <>
              <div style={sectionLabelStyle}>{t('events:filterSheet.allTeams')}</div>
              <ul style={listStyle}>
                {others.map((c) => (
                  <CountryRow
                    key={c.code}
                    country={c}
                    selected={selected.has(c.code)}
                    onToggle={() => toggle(c.code)}
                  />
                ))}
              </ul>
            </>
          )}
        </div>

        <div style={footerStyle}>
          <button
            type="button"
            onClick={clearAll}
            disabled={nothingActive}
            style={{
              ...clearButtonStyle,
              opacity: nothingActive ? 0.4 : 1,
              cursor: nothingActive ? 'default' : 'pointer',
            }}
          >
            {t('events:filterSheet.clear')}
          </button>
          <span style={countStyle}>
            {selected.size === 0
              ? t('events:filterSheet.noneSelected')
              : t('events:filterSheet.countSelected', { count: selected.size })}
          </span>
        </div>
      </div>
    </>
  );
}

function CountryRow({
  country,
  selected,
  onToggle,
}: {
  country: Country;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <li>
      <button type="button" onClick={onToggle} style={rowStyle}>
        <FlagStripe code={country.code} colors={country.colors} w={22} h={15} radius={2} />
        <span style={{ flex: 1, textAlign: 'left' }}>{country.name}</span>
        <span style={{ ...dotStyle, ...(selected ? dotActiveStyle(country.colors.primary) : {}) }} />
      </button>
    </li>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────

const backdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.55)',
  zIndex: 100,
};

const sheetStyle: CSSProperties = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  background: '#f0eee9',
  borderTopLeftRadius: 20,
  borderTopRightRadius: 20,
  maxHeight: '85vh',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 101,
  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  boxShadow: '0 -10px 30px rgba(0,0,0,0.18)',
};

const grabberStyle: CSSProperties = {
  width: 44,
  height: 4,
  background: '#d8d4cb',
  borderRadius: 999,
  margin: '10px auto 0',
};

const headerStyle: CSSProperties = {
  fontFamily: '-apple-system, system-ui',
  fontSize: 17,
  fontWeight: 600,
  color: '#0a0a0a',
  textAlign: 'center',
  margin: '10px 0 14px',
};

const contentStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '0 16px 16px',
};

const justMyTeamsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: '12px 16px',
  background: '#fff',
  border: '1px solid #ece8e2',
  borderRadius: 12,
  fontFamily: '-apple-system, system-ui',
  fontSize: 15,
  fontWeight: 600,
  color: '#0a0a0a',
  cursor: 'pointer',
  marginBottom: 18,
};

const justMyTeamsActiveStyle: CSSProperties = {
  background: '#0a0a0a',
  color: '#fff',
  border: '1px solid transparent',
};

const sectionLabelStyle: CSSProperties = {
  fontFamily: '-apple-system, system-ui',
  fontSize: 11,
  fontWeight: 700,
  color: '#8a7f72',
  textTransform: 'uppercase',
  letterSpacing: 1,
  padding: '0 4px 8px',
  marginTop: 6,
};

const whenRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginBottom: 18,
};

const whenChipStyle: CSSProperties = {
  background: 'transparent',
  color: '#5a544c',
  border: '1px solid #ddd7cd',
  padding: '6px 12px',
  borderRadius: 8,
  fontFamily: '-apple-system, system-ui',
  fontSize: 12.5,
  fontWeight: 600,
  letterSpacing: 0.1,
  cursor: 'pointer',
  transition: 'background .15s ease, color .15s ease, border-color .15s ease',
};

const whenChipActiveStyle: CSSProperties = {
  background: '#0a0a0a',
  color: '#fff',
  borderColor: '#0a0a0a',
  fontWeight: 700,
};

const listStyle: CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: '0 0 18px',
  background: '#fff',
  border: '1px solid #ece8e2',
  borderRadius: 12,
  overflow: 'hidden',
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  padding: '12px 14px',
  background: 'transparent',
  border: 'none',
  borderBottom: '1px solid #f3efe9',
  fontFamily: '-apple-system, system-ui',
  fontSize: 15,
  color: '#0a0a0a',
  cursor: 'pointer',
};

const dotStyle: CSSProperties = {
  width: 20,
  height: 20,
  borderRadius: 999,
  border: '1.5px solid #d8d4cb',
  background: 'transparent',
  flexShrink: 0,
};

function dotActiveStyle(primary: string): CSSProperties {
  return {
    background: primary,
    border: `1.5px solid ${primary}`,
  };
}

const footerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 20px',
  borderTop: '1px solid rgba(0,0,0,0.08)',
  background: '#f0eee9',
};

const clearButtonStyle: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#0a0a0a',
  fontFamily: '-apple-system, system-ui',
  fontSize: 14,
  fontWeight: 600,
  padding: 0,
};

const countStyle: CSSProperties = {
  fontFamily: '-apple-system, system-ui',
  fontSize: 13,
  fontWeight: 500,
  color: '#6f6a60',
};
